import { useTranslation } from 'react-i18next';

/**
 * Hook for attribute-related translations with type safety and error handling
 * Provides centralized access to all attribute management translations
 */
export function useAttributeTranslations() {
  const { t } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getModalText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.${key}`, params || {}));
  };

  const getValueEditorText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.valueEditor.${key}`, params || {}));
  };

  const getFieldText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.fields.${key}`, params || {}));
  };

  const getDataTypeText = (type: string, field: string = 'label'): string => {
    return String(t(`attributes.enhanced.modal.dataTypes.${type}.${field}`));
  };

  const getValidationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.validation.${key}`, params || {}));
  };

  const getToastText = (category: string, key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.toasts.${category}.${key}`, params || {}));
  };

  const getValueEditorToastText = (
    category: string,
    key: string,
    params?: Record<string, any>
  ): string => {
    return String(t(`attributes.enhanced.valueEditor.toasts.${category}.${key}`, params || {}));
  };

  const getMeasurementText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.measurement.${key}`, params || {}));
  };

  const getPriceText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.price.${key}`, params || {}));
  };

  const getRichTextText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.richText.${key}`, params || {}));
  };

  const getMediaText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.media.${key}`, params || {}));
  };

  const getButtonText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.buttons.${key}`, params || {}));
  };

  const getTestValidationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.modal.testValidation.${key}`, params || {}));
  };

  const getValueEditorActionText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.valueEditor.actions.${key}`, params || {}));
  };

  const getValueEditorPlaceholderText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.valueEditor.placeholders.${key}`, params || {}));
  };

  const getValueEditorValidationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.valueEditor.validation.${key}`, params || {}));
  };

  const getValueEditorDisplayText = (key: string, params?: Record<string, any>): string => {
    return String(t(`attributes.enhanced.valueEditor.display.${key}`, params || {}));
  };

  const getBooleanText = (key: string): string => {
    return String(t(`attributes.enhanced.valueEditor.boolean.${key}`));
  };

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, any>): string => {
    return String(t(key, params || {}));
  };

  // Get structured toast messages
  const getToastMessage = (
    category: 'modal' | 'valueEditor',
    type: string,
    key: string,
    params?: Record<string, any>
  ) => {
    const basePath =
      category === 'modal'
        ? 'attributes.enhanced.modal.toasts'
        : 'attributes.enhanced.valueEditor.toasts';
    const title = String(t(`${basePath}.${type}.title`, params || {}));
    const description = String(t(`${basePath}.${type}.description`, params || {}));

    return { title, description };
  };

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Category-specific helpers
    getModalText,
    getValueEditorText,
    getFieldText,
    getDataTypeText,
    getValidationText,
    getToastText,
    getValueEditorToastText,
    getMeasurementText,
    getPriceText,
    getRichTextText,
    getMediaText,
    getButtonText,
    getTestValidationText,
    getValueEditorActionText,
    getValueEditorPlaceholderText,
    getValueEditorValidationText,
    getValueEditorDisplayText,
    getBooleanText,

    // Structured helpers
    getToastMessage,
  };
}

export type AttributeTranslations = ReturnType<typeof useAttributeTranslations>;
